import { updateDebugStats } from '../utils/debug.js';
import { EndScreen } from '../ui/EndScreen.js';

export class UIManager {
  constructor() {
    this.statsEl = null;
    this.debugPanel = null;
    this.currentTeamEl = null;
    this.teamStatsEl = null;
    this.winScreenEl = null;
    this.gameTimerEl = null;
    this.statsWindow = null;
    this.endScreen = new EndScreen();

    // Create other UI elements
    this.createFPSDisplay();
  }

  // Called after StatsWindow is initialized
  setStatsWindow(statsWindow) {
    this.statsWindow = statsWindow;
    const elements = statsWindow.getElements();
    
    this.statsEl = elements.statsEl;
    this.debugPanel = elements.debugPanel;
    this.teamStatsEl = elements.teamStats;
    this.gameTimerEl = statsWindow.gameTimerEl;
    
    // Team stats are already created by StatsWindow, just need to populate
    if (this.teamStatsEl) {
      this.teamStatsEl.innerHTML = '<strong>Team Stats:</strong><br>';
    }
  }

  updateSoldierCount(activeSoldiers, maxUnits = null) {
    if (this.statsWindow) {
      this.statsWindow.updateSoldierCount(activeSoldiers, maxUnits);
    }
  }

  updateGameTimer(gameStartTime, isPaused = false) {
    if (this.statsWindow) {
      const gameTimeSeconds = Math.floor((Date.now() - gameStartTime) / 1000);
      const minutes = Math.floor(gameTimeSeconds / 60);
      const seconds = gameTimeSeconds % 60;
      const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      const displayStr = isPaused ? `${timeStr} (PAUSED)` : timeStr;
      this.statsWindow.updateGameTimer(displayStr);
    }
  }

  updateCurrentTeam(team) {
    if (this.currentTeamEl) {
      this.currentTeamEl.textContent = team.name;
      this.currentTeamEl.style.color = team.color;
    }
  }

  updateTeamStats(teamStats) {
    if (this.teamStatsEl) {
      let statsHtml = '<strong>Team Stats:</strong>';
      statsHtml += '<table class="team-stats-table">';
      statsHtml += '<thead><tr><th>Team</th><th>Units</th><th>Kills</th></tr></thead>';
      statsHtml += '<tbody>';
      
      teamStats.forEach(team => {
        if (team.units > 0 || team.kills > 0) {
          statsHtml += `<tr>`;
          statsHtml += `<td class="team-name" style="color: ${team.color};">${team.name}</td>`;
          statsHtml += `<td class="team-units">${team.units}</td>`;
          statsHtml += `<td class="team-kills">${team.kills}</td>`;
          statsHtml += `</tr>`;
        }
      });
      
      statsHtml += '</tbody></table>';
      this.teamStatsEl.innerHTML = statsHtml;
    }
  }

  updateDebugInfo(stats) {
    updateDebugStats(stats);
  }

  showDebugPanel(show) {
    if (this.debugPanel) {
      this.debugPanel.style.display = show ? 'block' : 'none';
    }
  }

  showWinScreen(team, stats, onRestart, forgeSessionInfo = null) {
    this.endScreen.show(team, stats, onRestart, forgeSessionInfo);
  }
  
  createFPSDisplay() {
    const fpsDiv = document.createElement('div');
    fpsDiv.id = 'fpsDisplay';
    fpsDiv.style.position = 'fixed';
    fpsDiv.style.top = '10px';
    fpsDiv.style.right = '10px';
    fpsDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    fpsDiv.style.color = '#0f0';
    fpsDiv.style.padding = '5px 10px';
    fpsDiv.style.fontFamily = "'Berkeley Mono', 'Courier New', monospace";
    fpsDiv.style.fontSize = '14px';
    fpsDiv.style.borderRadius = '3px';
    fpsDiv.style.border = '1px solid #0f0';
    fpsDiv.innerHTML = 'FPS: <span id="fpsValue">0</span>';
    document.body.appendChild(fpsDiv);
    
    this.fpsValueEl = document.getElementById('fpsValue');
    this.frameTimes = [];
    this.lastFPSUpdate = 0;
    
    // Create paused display
    this.createPausedDisplay();
    
    // Create renderer display
    this.createRendererDisplay();
    
    // Create spawn type display
    this.createSpawnTypeDisplay();
  }
  
  createRendererDisplay() {
    const rendererDiv = document.createElement('div');
    rendererDiv.id = 'rendererDisplay';
    rendererDiv.style.position = 'fixed';
    rendererDiv.style.top = '10px';
    rendererDiv.style.left = '10px';
    rendererDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    rendererDiv.style.color = '#0f0';
    rendererDiv.style.padding = '5px 10px';
    rendererDiv.style.fontFamily = "'Berkeley Mono', 'Courier New', monospace";
    rendererDiv.style.fontSize = '14px';
    rendererDiv.style.borderRadius = '3px';
    rendererDiv.style.border = '1px solid #0f0';
    rendererDiv.style.cursor = 'pointer';
    rendererDiv.style.userSelect = 'none';
    rendererDiv.innerHTML = 'Renderer: <span id="rendererValue" style="font-weight: bold;">2D</span> <span style="font-size: 11px; opacity: 0.7;">(R/tap)</span>';
    document.body.appendChild(rendererDiv);
    
    this.rendererValueEl = document.getElementById('rendererValue');
    this.rendererDiv = rendererDiv;
  }
  
  createSpawnTypeDisplay() {
    const spawnDiv = document.createElement('div');
    spawnDiv.id = 'spawnTypeDisplay';
    spawnDiv.style.position = 'fixed';
    spawnDiv.style.top = '10px';
    spawnDiv.style.left = '50%';
    spawnDiv.style.transform = 'translateX(-50%)';
    spawnDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    spawnDiv.style.color = '#0f0';
    spawnDiv.style.padding = '5px 15px';
    spawnDiv.style.fontFamily = "'Berkeley Mono', 'Courier New', monospace";
    spawnDiv.style.fontSize = '14px';
    spawnDiv.style.borderRadius = '3px';
    spawnDiv.style.border = '1px solid #0f0';
    spawnDiv.style.cursor = 'pointer';
    spawnDiv.style.userSelect = 'none';
    spawnDiv.innerHTML = 'Spawn: <span id="spawnTypeValue" style="font-weight: bold;">Red</span> <span style="font-size: 11px; opacity: 0.7;">(T/tap)</span>';
    document.body.appendChild(spawnDiv);
    
    this.spawnTypeValueEl = document.getElementById('spawnTypeValue');
    this.spawnDiv = spawnDiv;
  }
  
  createPausedDisplay() {
    const pausedDiv = document.createElement('div');
    pausedDiv.id = 'pausedDisplay';
    pausedDiv.style.position = 'fixed';
    pausedDiv.style.top = '60px';
    pausedDiv.style.left = '50%';
    pausedDiv.style.transform = 'translateX(-50%)';
    pausedDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    pausedDiv.style.color = '#ff0';
    pausedDiv.style.padding = '10px 20px';
    pausedDiv.style.fontFamily = "'Berkeley Mono', 'Courier New', monospace";
    pausedDiv.style.fontSize = '24px';
    pausedDiv.style.fontWeight = 'bold';
    pausedDiv.style.borderRadius = '5px';
    pausedDiv.style.border = '2px solid #ff0';
    pausedDiv.style.display = 'none';
    pausedDiv.style.textAlign = 'center';
    pausedDiv.innerHTML = 'PAUSED<br><span style="font-size: 14px; font-weight: normal;">Press SPACE to resume</span>';
    document.body.appendChild(pausedDiv);
    
    this.pausedDiv = pausedDiv;
  }
  
  updatePausedState(isPaused) {
    if (this.pausedDiv) {
      this.pausedDiv.style.display = isPaused ? 'block' : 'none';
    }
  }
  
  updateSpawnType(spawnType) {
    if (!this.spawnTypeValueEl) return;
    
    let displayText = '';
    let displayColor = '#0f0';
    
    if (spawnType.type === 'soldier') {
      displayText = spawnType.team.name;
      displayColor = spawnType.team.color;
    } else {
      displayText = spawnType.name;
      // Color resources appropriately
      switch(spawnType.type) {
        case 'metal':
          displayColor = '#aaa';
          break;
        case 'crystal':
          displayColor = '#0ff';
          break;
        case 'nebula':
          displayColor = '#f0f';
          break;
      }
    }
    
    this.spawnTypeValueEl.textContent = displayText;
    this.spawnTypeValueEl.style.color = displayColor;
  }
  
  updateRenderer(rendererType) {
    if (!this.rendererValueEl) return;
    
    let displayText = '';
    let displayColor = '#0f0';
    
    if (rendererType === 'threejs') {
      displayText = '3D';
      displayColor = '#0ff'; // Cyan for 3D
    } else {
      displayText = '2D';
      displayColor = '#0f0'; // Green for 2D
    }
    
    this.rendererValueEl.textContent = displayText;
    this.rendererValueEl.style.color = displayColor;
  }
  
  updateFPS(currentTime) {
    if (!this.lastFrameTime) {
      this.lastFrameTime = currentTime;
      return;
    }
    
    const deltaTime = currentTime - this.lastFrameTime;
    this.lastFrameTime = currentTime;
    
    // Store frame times for averaging
    this.frameTimes.push(deltaTime);
    if (this.frameTimes.length > 60) {
      this.frameTimes.shift();
    }
    
    // Update display every 500ms
    if (currentTime - this.lastFPSUpdate > 500) {
      const avgFrameTime = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
      const fps = Math.round(1000 / avgFrameTime);
      
      if (this.fpsValueEl) {
        this.fpsValueEl.textContent = fps;
        
        // Color code based on performance
        if (fps >= 50) {
          this.fpsValueEl.style.color = '#0f0'; // Green
        } else if (fps >= 30) {
          this.fpsValueEl.style.color = '#ff0'; // Yellow
        } else {
          this.fpsValueEl.style.color = '#f00'; // Red
        }
      }
      
      this.lastFPSUpdate = currentTime;
    }
  }
  
  getRendererDiv() {
    return this.rendererDiv;
  }
  
  getSpawnDiv() {
    return this.spawnDiv;
  }
  
  // === DEVELOPMENT COMMANDS ===
  
  // Show all UI elements
  showAllUI() {
    const elements = [
      this.endScreen.container,
      this.pausedDiv,
      this.rendererDiv,
      this.spawnDiv,
      document.getElementById('fpsDisplay')
    ];
    
    let shown = 0;
    elements.forEach(el => {
      if (el && el.style.display === 'none') {
        el.style.display = el.id === 'endScreen' ? 'flex' : 'block';
        shown++;
      }
    });
    
    console.log(`📊 Shown ${shown} hidden UI elements`);
  }
  
  // Hide all UI overlays (except stats)
  hideAllOverlays() {
    const elements = [
      this.endScreen.container,
      this.pausedDiv
    ];
    
    let hidden = 0;
    elements.forEach(el => {
      if (el && el.style.display !== 'none') {
        el.style.display = 'none';
        hidden++;
      }
    });
    
    console.log(`📊 Hidden ${hidden} overlay elements`);
  }
  
  // Toggle FPS display
  toggleFPS() {
    const fpsDisplay = document.getElementById('fpsDisplay');
    if (fpsDisplay) {
      const isVisible = fpsDisplay.style.display !== 'none';
      fpsDisplay.style.display = isVisible ? 'none' : 'block';
      console.log(`📊 FPS display: ${isVisible ? 'hidden' : 'shown'}`);
      return !isVisible;
    }
    return false;
  }
  
  // Force show win screen for testing
  testWinScreen(teamName = 'RED') {
    const teams = {
      RED: { name: 'Red', color: '#f00' },
      BLUE: { name: 'Blue', color: '#00f' },
      GREEN: { name: 'Green', color: '#0f0' },
      YELLOW: { name: 'Yellow', color: '#ff0' },
      PINK: { name: 'Pink', color: '#f0f' }
    };
    
    const team = teams[teamName.toUpperCase()] || teams.RED;

    const mockStats = [
        { name: 'RED', color: '#f00', units: 10, kills: 20 },
        { name: 'BLUE', color: '#00f', units: 5, kills: 15 },
        { name: 'GREEN', color: '#0f0', units: 8, kills: 8 }
    ];

    this.showWinScreen(team, mockStats, () => {
      console.log('Restart button clicked');
      this.endScreen.hide();
    });
    
    console.log(`📊 Showing win screen for ${team.name} team`);
  }
  
  // Test pause display
  testPause(duration = 3000) {
    this.updatePausedState(true);
    console.log(`📊 Showing pause display for ${duration}ms`);
    
    setTimeout(() => {
      this.updatePausedState(false);
      console.log('📊 Pause display hidden');
    }, duration);
  }
  
  // Update all stats for testing
  testStats() {
    // Test soldier count with different states
    const tests = [
      { count: 50, max: 100 },   // Normal
      { count: 85, max: 100 },   // Warning
      { count: 100, max: 100 }   // Full
    ];
    
    let index = 0;
    const interval = setInterval(() => {
      if (index >= tests.length) {
        clearInterval(interval);
        console.log('📊 Stats test complete');
        return;
      }
      
      const test = tests[index];
      this.updateSoldierCount(test.count, test.max);
      console.log(`📊 Updated soldier count: ${test.count}/${test.max}`);
      index++;
    }, 1500);
    
    // Test team stats
    const mockTeamStats = [
      { name: 'RED', color: '#f00', units: 25, kills: 12 },
      { name: 'BLUE', color: '#00f', units: 20, kills: 15 },
      { name: 'GREEN', color: '#0f0', units: 15, kills: 8 },
      { name: 'YELLOW', color: '#ff0', units: 10, kills: 5 },
      { name: 'PINK', color: '#f0f', units: 30, kills: 20 }
    ];
    
    this.updateTeamStats(mockTeamStats);
    console.log('📊 Updated team stats with mock data');
  }
  
  // Get current UI state
  getUIState() {
    const state = {
      paused: this.pausedDiv?.style.display !== 'none',
      winScreen: this.endScreen.container?.style.display !== 'none',
      fpsVisible: document.getElementById('fpsDisplay')?.style.display !== 'none',
      renderer: this.rendererValueEl?.textContent || 'Unknown',
      spawnType: this.spawnTypeValueEl?.textContent || 'Unknown',
      soldierCount: this.soldierCountEl?.textContent || '0',
      gameTime: this.gameTimerEl?.textContent || '0:00'
    };
    
    console.group('📊 Current UI State');
    Object.entries(state).forEach(([key, value]) => {
      console.log(`${key}: ${value}`);
    });
    console.groupEnd();
    
    return state;
  }
  
  // List all UI elements
  listElements() {
    const elements = {
      'Win Screen': this.endScreen.container,
      'Paused Display': this.pausedDiv,
      'FPS Display': document.getElementById('fpsDisplay'),
      'Renderer Display': this.rendererDiv,
      'Spawn Type Display': this.spawnDiv,
      'Stats Window': this.statsWindow?.container,
      'Debug Panel': this.debugPanel
    };
    
    console.group('📊 UI Elements');
    Object.entries(elements).forEach(([name, el]) => {
      if (el) {
        const rect = el.getBoundingClientRect();
        console.log(`${name}:`, {
          id: el.id,
          visible: el.style.display !== 'none',
          position: `${rect.left},${rect.top}`,
          size: `${rect.width}x${rect.height}`
        });
      } else {
        console.log(`${name}: Not initialized`);
      }
    });
    console.groupEnd();
  }
  
  // Cycle spawn type for testing
  cycleSpawnType() {
    // This would need to be connected to the actual spawn system
    const types = [
      { type: 'soldier', team: { name: 'Red', color: '#f00' } },
      { type: 'soldier', team: { name: 'Blue', color: '#00f' } },
      { type: 'soldier', team: { name: 'Green', color: '#0f0' } },
      { type: 'metal', name: 'Metal' },
      { type: 'crystal', name: 'Crystal' },
      { type: 'nebula', name: 'Nebula' }
    ];
    
    // Get current type and find next
    const current = this.spawnTypeValueEl?.textContent || 'Red';
    let index = types.findIndex(t => 
      t.type === 'soldier' ? t.team.name === current : t.name === current
    );
    index = (index + 1) % types.length;
    
    this.updateSpawnType(types[index]);
    console.log(`📊 Spawn type cycled to: ${types[index].name || types[index].team.name}`);
  }
  
  // Toggle renderer display for testing
  cycleRenderer() {
    const current = this.rendererValueEl?.textContent || '2D';
    const next = current === '2D' ? 'threejs' : 'canvas2d';
    this.updateRenderer(next);
    console.log(`📊 Renderer display updated to: ${next === 'threejs' ? '3D' : '2D'}`);
  }
}